/**
 * @param {Object} target
 * @param {...Object} others
 * @return {Object}
 */
function mergeObjects(target, ...others)
{
    for (let other of others) {
        Object.keys(other).forEach((key) => {
            if (other.hasOwnProperty(key)) {
                target[key] = other[key];
            }
        });
    }

    return target;
}

/**
 * @param {Element} element
 * @param {Array} keyframes
 * @param {Object} options
 */
function animate(element, keyframes, options)
{
    if(typeof element.animate !== 'function') {
        return;
    }

    element.animate(keyframes, options);
}

/**
 * @param {String} htmlString
 * @param {String} parentTag
 * @return {Element}
 */
function createElement(htmlString, parentTag = 'div')
{
    let element = document.createElement(parentTag);
    element.innerHTML = htmlString;

    return element;
}

/**
 * @param {Element} element
 * @param {Element} parent
 */
function appendElement(element, parent)
{
    parent.append(element);
    animate(
        element,
        [
            {display: 'none', opacity: 0},
            {display: '', opacity: 1}
        ],
        {
            duration: 500,
            iterations: 1,
        }
    );
}

/**
 * @param {Element} parentElement
 * @param {String} eventType
 * @param {String} childSelector
 * @param {function(Element|EventTarget, Event)} eventCallback
 */
function addDelegateEventListener(parentElement, eventType, childSelector, eventCallback)
{
    parentElement.addEventListener(eventType, (event) => {
        let targetElement = event.target;

        while (targetElement && targetElement !== parentElement) {
            if (targetElement.matches(childSelector)) {
                eventCallback(targetElement, event);
            }

            targetElement = targetElement.parentNode;
        }
    });
}

/**
 * @param {Element} element
 */
function flashWithoutAnimation(element)
{
    element.style.opacity = 1;
    element.style.display = 'block';

    setTimeout(() => {
        element.style.opacity = 0;
        element.style.display = 'none';
    }, 1000);
}

/**
 * @param {Element} element
 */
function flash(element)
{
    if (typeof element.animate !== 'function') {
        flashWithoutAnimation(element);
        return;
    }

    animate(
        element,
        [
            {display: 'none', opacity: 0},
            {display: '', opacity: 1}
        ],
        {
            duration: 1000,
            iterations: 1,
            fill: 'forwards',
        }
    );

    animate(
        element,
        [
            {display: '', opacity: 1},
            {display: 'none', opacity: 0}
        ],
        {
            delay: 2000,
            duration: 400,
            iterations: 1,
            fill: 'forwards',
        }
    );
}

/**
 * @param {Element} element
 */
function slideDown(element)
{
    animate(
        element,
        [
            {height: '0px'},
            {height: element.offsetHeight + 'px'}
        ],
        {
            duration: 500,
            iterations: 1,
            fill: 'forwards',
        }
    );
}

/**
 * @param {NodeListOf<Element>} list
 * @param {Element} element
 * @param {Number} index
 */
function setToList(list, element, index)
{
    let setAfterItem;

    if (list.length > 1) {
        setAfterItem = list[index - 1];
    } else if (list.length === 1) {
        setAfterItem = list[0];
    }

    if (setAfterItem && setAfterItem.parentNode) {
        setAfterItem.parentNode.insertBefore(element, setAfterItem.nextSibling);
    }
}

function getIOSVersion()
{
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
        let regex = /OS (\d+)_(\d+)_?(\d+)?/i;
        let version = navigator.appVersion.match(regex);

        return [
            parseInt(version[1], 10),
            parseInt(version[2], 10),
            parseInt(version[3] || 0, 10)
        ];
    }

    return false;
}

/**
 * @param {Element} element
 * @param {String} attributeName
 * @param {Boolean} toggleStatus
 * @return {Boolean}
 */
function toggleAttribute(element, attributeName, toggleStatus)
{
    if (element.hasAttribute(attributeName)) {
        if (toggleStatus) {
            return true;
        }

        element.removeAttribute(attributeName);
        return false;
    }

    if (toggleStatus === false) {
        return false;
    }

    element.setAttribute(attributeName, '');
    return true;
}

export {mergeObjects, createElement, appendElement, addDelegateEventListener, flash, slideDown, setToList, getIOSVersion, toggleAttribute};
